import { useEffect, useRef, useCallback } from "react";

interface FocusTrapOptions {
  enabled: boolean;
  containerRef: React.RefObject<HTMLElement>;
  shadowRoot?: ShadowRoot | null;
  onEscape?: () => void;
  initialFocusSelector?: string;
}

// Focusable element selector
const FOCUSABLE_SELECTOR = [
  "a[href]",
  "button:not([disabled])",
  "textarea:not([disabled])",
  'input:not([disabled]):not([type="hidden"])',
  "select:not([disabled])",
  '[tabindex]:not([tabindex="-1"])',
].join(", ");

export const useFocusTrap = ({
  enabled,
  containerRef,
  shadowRoot,
  onEscape,
  initialFocusSelector,
}: FocusTrapOptions) => {
  const previouslyFocusedElement = useRef<HTMLElement | null>(null);

  // Get all focusable elements within the container (Shadow DOM aware)
  const getFocusableElements = useCallback((): HTMLElement[] => {
    if (!containerRef.current) return [];

    // If we have a shadow root, query within it
    const root = shadowRoot || containerRef.current;
    const elements = root.querySelectorAll(FOCUSABLE_SELECTOR);

    return Array.from(elements).filter((el): el is HTMLElement => {
      return (
        el instanceof HTMLElement &&
        !el.hasAttribute("disabled") &&
        el.offsetParent !== null // Check if element is visible
      );
    });
  }, [containerRef, shadowRoot]);

  // Focus the first focusable element or specific element by selector
  const focusFirstElement = useCallback(
    (selector?: string) => {
      const focusableElements = getFocusableElements();

      if (selector) {
        // Try to find element by selector first
        const root = shadowRoot || containerRef.current;
        const targetElement = root?.querySelector(selector);
        if (
          targetElement instanceof HTMLElement &&
          focusableElements.includes(targetElement)
        ) {
          targetElement.focus();
          return;
        }
      }

      // Fallback to first focusable element
      if (focusableElements.length > 0) {
        focusableElements[0].focus();
      }
    },
    [getFocusableElements, shadowRoot, containerRef],
  );

  // Handle tab key navigation
  const handleTabKey = useCallback(
    (e: KeyboardEvent) => {
      if (!enabled || e.key !== "Tab") return;

      const focusableElements = getFocusableElements();
      if (focusableElements.length === 0) return;

      const firstElement = focusableElements[0];
      const lastElement = focusableElements[focusableElements.length - 1];
      const activeElement = shadowRoot?.activeElement || document.activeElement;

      // Handle Shift+Tab (backwards)
      if (e.shiftKey) {
        if (activeElement === firstElement) {
          e.preventDefault();
          lastElement.focus();
        }
      } else {
        // Handle Tab (forwards)
        if (activeElement === lastElement) {
          e.preventDefault();
          firstElement.focus();
        }
      }
    },
    [enabled, getFocusableElements, shadowRoot],
  );

  // Handle escape key
  const handleEscapeKey = useCallback(
    (e: KeyboardEvent) => {
      if (enabled && e.key === "Escape" && onEscape) {
        onEscape();
      }
    },
    [enabled, onEscape],
  );

  // Set up focus trap
  useEffect(() => {
    if (!enabled) return;

    // Save currently focused element
    previouslyFocusedElement.current = document.activeElement as HTMLElement;

    // Focus the initial element
    requestAnimationFrame(() => {
      focusFirstElement(initialFocusSelector);
    });

    // Add event listeners
    document.addEventListener("keydown", handleTabKey);
    document.addEventListener("keydown", handleEscapeKey);

    return () => {
      document.removeEventListener("keydown", handleTabKey);
      document.removeEventListener("keydown", handleEscapeKey);

      // Restore focus to previously focused element
      if (previouslyFocusedElement.current) {
        previouslyFocusedElement.current.focus();
      }
    };
  }, [
    enabled,
    handleTabKey,
    handleEscapeKey,
    focusFirstElement,
    initialFocusSelector,
  ]);

  return {
    focusFirstElement,
    getFocusableElements,
  };
};
